/**
 * PATH TO GO — the ONE definition of "which ranked steps are evidence work",
 * plus the ONE mapping from a step to the single verb that resolves it.
 *
 * WHY THIS MODULE EXISTS. Path to GO's Evidence stage and the Site Evidence tab
 * were two hand-maintained views of the same work. The wizard filtered
 * `rankedSteps` with an inline predicate; the Evidence tab derived its own list
 * from `documentChecklists` rows instead. The two therefore disagreed in the
 * field — on Redhead Industrial Park (2026-07-25) Path to GO reported
 * "4 open · 1 GO-blocking" while the Evidence tab reported "80% READY · 0
 * Missing" for the same site, at the same moment.
 *
 * This mirrors the fix already applied to the Analysis Matrix ↔ Analyse stage
 * pair in 95e5c872: both surfaces read the SAME selector and dispatch the SAME
 * action, so their labels and destinations are identical BY CONSTRUCTION rather
 * than by two lists that drift.
 *
 * Pure + deterministic. No imports beyond the NextStep type.
 */
import type { NextStep } from './nextStep';
import { requirementIdForBareFocusId } from '../../config/requirementToFields';

/**
 * Step ids that belong to another stage even though they survive the
 * `kind !== 'intent'` cut. Kept as a named set so adding one is a one-line,
 * reviewable change rather than an edit to an inline boolean expression.
 *
 * - `site-basis:boundary` → resolved on the Map tab (Define phase).
 * - `generation:testfit`  → resolved in Generation & Impact (Analyse phase).
 */
const NON_EVIDENCE_STEP_IDS: ReadonlySet<string> = new Set([
  'site-basis:boundary',
  'generation:testfit',
]);

/**
 * The evidence worklist: every ranked step a user resolves on the Site Evidence
 * surface. Order is preserved — `rankSteps` has already sorted critical-first,
 * so the caller must NOT re-sort.
 *
 * `kind === 'intent'` steps are excluded because they are resolved in the
 * Project Intent editor, not against evidence.
 */
export function selectEvidenceSteps(
  steps: readonly NextStep[],
  waivedRequirementIds?: ReadonlySet<string>,
): NextStep[] {
  return steps.filter(step => {
    if (step.kind === 'intent' || NON_EVIDENCE_STEP_IDS.has(step.id)) return false;
    if (waivedRequirementIds && waivedRequirementIds.size > 0
      && isStepWaived(step, waivedRequirementIds)) return false;
    return true;
  });
}

/**
 * Has this step been dispositioned by a waiver?
 *
 * Waivers are keyed by CHECKLIST REQUIREMENT, but a preflight step names a SED
 * SECTION (`preflight:publicSentiment`). Without the bridge, recording "I
 * checked, the source has no record" left the gap sitting on the worklist
 * demanding the very thing the user had just attested was unobtainable — the
 * waiver persisted and changed nothing.
 *
 * `requirementIdForBareFocusId` is the same resolver the deep-link and the
 * Author action use, so `publicSentiment` reaches `community-feedback-summary`
 * here exactly as it does there.
 *
 * A waived step is REMOVED from the worklist, not marked satisfied. The waiver
 * itself remains visible in the Waived gaps register with its reason and
 * author. Nothing here upgrades a readiness score: an attested absence is a
 * disclosed limitation, not evidence.
 */
export function isStepWaived(step: NextStep, waivedRequirementIds: ReadonlySet<string>): boolean {
  const targetId = step.resolver?.targetId;
  if (!targetId) return false;
  // Direct hit — the step already names a requirement.
  if (waivedRequirementIds.has(targetId)) return true;
  // A SED section id resolves through the shared bridge. Dotted paths are
  // individual fields and never carry a waiver of their own.
  if (targetId.includes('.')) return false;
  const requirementId = requirementIdForBareFocusId(targetId);
  return requirementId != null && waivedRequirementIds.has(requirementId);
}

/** A preflight-derived source may affect the final verdict, but never blocks opening Analyze. */
export function isGoBlockingStep(step: NextStep): boolean {
  return step.decisionRequired ?? step.sourceModule === 'preflight';
}

/**
 * A step whose documents are ALREADY uploaded but whose extraction has not
 * confirmed the required values. These must never be labelled "Upload" — the
 * user has provided the document and being asked for it again is the single
 * most confusing state on the surface.
 */
export function isVerifyStep(step: NextStep): boolean {
  return (step.matchedDocNames?.length ?? 0) > 0;
}

/** How the ledger should resolve a step. Drives label AND destination. */
export type EvidenceVerbKind =
  | 'recollect'   // provider section never collected — re-run collection
  | 'verify'      // documents present AND extracted — check the values
  | 'author'      // documents present but extraction found nothing — supply it
  | 'upload'      // no document — upload one
  | 'intent'      // resolved in the Project Intent editor
  | 'open';       // no specific verb — open the requirement

/**
 * What the caller knows about the step's underlying evidence.
 *
 * Kept as an explicit argument rather than read inside this module because
 * these functions are pure and deterministic by design — every surface reads
 * the same verb for the same inputs, and a hidden SED read here would break
 * that guarantee.
 */
export interface EvidenceVerbContext {
  /**
   * Did extraction actually populate any field for this requirement?
   *
   * `false` is materially different from `undefined`: false means we checked
   * and there is nothing, undefined means we do not know and the verb should
   * keep its historical behaviour.
   */
  hasExtractedFields?: boolean;
}

export interface EvidenceVerb {
  kind: EvidenceVerbKind;
  /** Primary button label — imperative, one action. */
  label: string;
  /** Optional secondary affordance label. Omitted when there isn't a real one. */
  secondaryLabel?: string;
  /**
   * Optional "Author the answer" affordance, rendered between the secondary
   * and Waive. Present on gaps where the user may simply KNOW the answer and
   * should not be forced to manufacture a document to record it — a survey's
   * facts can arrive in a surveyor's email long before the signed PDF does.
   * Absent on `verify` (extracted values already have the inline review) and
   * on `recollect`/`author`, where authoring IS already the secondary/primary.
   */
  authorLabel?: string;
  /**
   * Optional THIRD affordance — in practice always "Waive…".
   *
   * Every evidence gap can turn out to be unobtainable, not just a provider
   * one: a document may not exist, an extraction may never confirm, a
   * requirement may not apply to this site. So the disposition is offered on
   * every verb EXCEPT intent, which is not an evidence gap at all — project
   * intent is authored by the user by definition and can never be "unavailable".
   */
  tertiaryLabel?: string;
}

/**
 * Derive the single next verb for a step.
 *
 * ORDER MATTERS and encodes the product judgement:
 *
 *   1. Already-uploaded documents win over everything except intent. If the
 *      user has given us the file, the ask is ALWAYS "verify", never "upload".
 *   2. A preflight step is a provider section that did not collect — no amount
 *      of uploading fixes it, so it asks for a re-run.
 *   3. An outreach-capable requirement is a document we do not have.
 *   4. Everything else just opens.
 *
 * NOTE FOR REVIEW: this mapping is the one genuinely opinionated part of the
 * ledger. If the ordering above does not match how the diligence team actually
 * works, change it HERE — every surface reads this function.
 */
export function evidenceStepVerb(step: NextStep, context?: EvidenceVerbContext): EvidenceVerb {
  if (step.resolver?.action === 'open-intent') {
    return { kind: 'intent', label: step.resolver.label ?? 'Edit project intent' };
  }
  if (isVerifyStep(step)) {
    // A document is attached but extraction produced NOTHING. "Verify
    // extracted values" is then an instruction to check values that do not
    // exist — the user clicks it and finds an empty worksheet. Observed on
    // Redhead's mineral-title row: three documents attached, zero fields
    // populated.
    //
    // The honest asks in that state are to supply the answer yourself, or to
    // provide a document the extractor can actually read. Re-running
    // extraction on the same document that already yielded nothing is the
    // least likely of the three to help, so it stops being offered.
    if (context?.hasExtractedFields === false) {
      return {
        kind: 'author',
        label: 'Author the answer',
        secondaryLabel: 'Upload another document',
        tertiaryLabel: 'Waive…',
      };
    }
    return {
      kind: 'verify',
      label: 'Verify extracted values',
      secondaryLabel: 'Re-run extraction',
      tertiaryLabel: 'Waive…',
    };
  }
  if (isGoBlockingStep(step)) {
    return {
      kind: 'recollect',
      label: 'Re-run collection',
      // A provider gap has three honest endings, and until now the ledger only
      // offered one. Re-running fixes a provider that FAILED; it does nothing
      // for a source that genuinely holds no record of this site, which left
      // the user staring at an unclosable GO-blocker with no way to say "I
      // checked, there is nothing, here is what I know".
      //
      //   Author — you have the answer the provider could not fetch.
      //   Waive  — the answer does not exist, and that is itself a finding.
      secondaryLabel: 'Author the answer',
      tertiaryLabel: 'Waive…',
    };
  }
  if (step.outreachId) {
    return {
      kind: 'upload',
      label: 'Upload document',
      secondaryLabel: 'Draft email request',
      // The document is the PREFERRED evidence, but its facts may already be
      // in hand (a surveyor's email, a paper copy). Authoring records them
      // as user-typed rather than blocking on a file upload.
      authorLabel: 'Author the answer',
      tertiaryLabel: 'Waive…',
    };
  }
  return {
    kind: 'open',
    label: step.resolver?.label ?? 'Open requirement',
    authorLabel: 'Author the answer',
    tertiaryLabel: 'Waive…',
  };
}

/**
 * The action sentence that belongs after a verify-family step's `why`.
 *
 * The step's base text states the FACT (documents uploaded, extraction not
 * confirmed); this states the ASK — and the ask depends on what extraction
 * actually produced, which only the renderer knows. Deriving it from the
 * resolved verb keeps the prose and the buttons in agreement BY CONSTRUCTION:
 * the founder caught the old hardcoded text telling users to "Re-run
 * collection" on cards whose buttons said Author / Upload another / Waive.
 *
 * Returns null for verbs whose `why` already carries the right ask (provider
 * gaps explain themselves via preflightRedHelp; upload gaps say "fill it").
 */
export function evidenceStepActionHint(verb: EvidenceVerb): string | null {
  if (verb.kind === 'author') {
    return 'Extraction found none of the required fields in these documents — '
      + 're-reading them will not help. Author the answer yourself, provide a '
      + 'different document, or waive the gap.';
  }
  if (verb.kind === 'verify') {
    return 'Check the extracted values below against the document, then '
      + 'confirm or correct each one.';
  }
  return null;
}

/**
 * Resolve a deep-link focus token to a step in the ledger.
 *
 * Path to GO deep-links carry `resolver.targetId`, which may be a checklist
 * requirement id ('geotech'), a SED section id ('publicSentiment'), or absent.
 * The old receiver routed these through `focus=variable:<id>` into the
 * Extracted Variables pane, where a SED SECTION id has no row to land on — the
 * user was sent to fix `publicSentiment` and shown 23 domains, none of them it.
 *
 * Matching against the step list instead means the target ALWAYS resolves,
 * because the ledger renders the very list the deep-link was generated from.
 * Falls back to a prefix/suffix match so a bare id still finds its namespaced
 * step (`publicSentiment` → `preflight:publicSentiment`).
 */
export function findEvidenceStepByTarget(
  steps: readonly NextStep[],
  targetId: string | null | undefined,
): NextStep | null {
  if (!targetId) return null;
  const exact = steps.find(s => s.id === targetId);
  if (exact) return exact;
  const byTarget = steps.find(s => s.resolver?.targetId === targetId);
  if (byTarget) return byTarget;
  // Bare id → namespaced step id ('publicSentiment' → 'preflight:publicSentiment').
  const bare = steps.find(s => s.id.endsWith(`:${targetId}`));
  return bare ?? null;
}
